import { z } from "zod";
import { daysBetween } from "../zoned";

/** The fixed priorities, highest first, so priority means the same in every project (BUILD_SPEC §10.4). */
export const ISSUE_PRIORITIES = ["urgent", "high", "medium", "low", "none"] as const;
export const issuePrioritySchema = z.enum(ISSUE_PRIORITIES);
export type IssuePriority = z.infer<typeof issuePrioritySchema>;

/** The six status categories every status belongs to (BUILD_SPEC §11.1), in workflow order. */
export const STATUS_CATEGORIES = ["triage", "backlog", "unstarted", "started", "completed", "cancelled"] as const;
export const statusCategorySchema = z.enum(STATUS_CATEGORIES);
export type StatusCategory = z.infer<typeof statusCategorySchema>;

/** Whether an issue in a status of this category is resolved: Completed or Cancelled (BUILD_SPEC §11.1). */
export function isResolvedCategory(category: StatusCategory): boolean {
  return category === "completed" || category === "cancelled";
}

/** Steer's built-in issue types (BUILD_SPEC §10.5); a project's custom types are `c_<id>`. */
export const BUILT_IN_TYPES = ["epic", "story", "task", "bug", "subtask"] as const;
export type BuiltInType = (typeof BUILT_IN_TYPES)[number];
export const issueTypeKeySchema = z
  .string()
  .regex(/^(?:epic|story|task|bug|subtask|c_[a-z0-9]{6,32})$/, "Unknown type");

/** An id Steer made: a UUID. */
export const idSchema = z
  .string()
  .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/, "Not an id Steer knows");

/** A person, by Impa's id for them. */
export const personIdSchema = z.string().min(1).max(64);

/** A calendar date with no time, `YYYY-MM-DD`, that exists. */
export const calendarDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Dates are YYYY-MM-DD")
  .refine((text) => {
    const date = new Date(`${text}T00:00:00Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === text;
  }, "That date does not exist");

export const issueTitleSchema = z.string().trim().min(1, "Title is required").max(255);
export const issueDescriptionSchema = z.string().trim().max(100_000);
export const estimateSchema = z.number().min(0).max(999);

export const createIssueSchema = z
  .object({
    title: issueTitleSchema,
    description: issueDescriptionSchema.optional(),
    type: issueTypeKeySchema.optional(),
    statusId: idSchema.optional(),
    priority: issuePrioritySchema.optional(),
    assigneeId: personIdSchema.nullable().optional(),
    parentId: idSchema.nullable().optional(),
    labelIds: z.array(idSchema).max(20).optional(),
    startDate: calendarDateSchema.nullable().optional(),
    dueDate: calendarDateSchema.nullable().optional(),
    estimate: estimateSchema.nullable().optional(),
    /** A Scrum project's sprint it is planned in, or null for the backlog (BUILD_SPEC §14.1). */
    sprintId: idSchema.nullable().optional(),
    /** The project's release it is to ship in (§14.2), or none. */
    releaseId: idSchema.nullable().optional(),
    /** Placed straight after this issue in the project's order, or first for null; last when not given. */
    after: idSchema.nullable().optional(),
  })
  .strict();

/** A change to an issue's fields, made against the revision the person had (BUILD_SPEC §23.4). */
export const updateIssueSchema = z
  .object({
    revision: z.number().int().positive().optional(),
    title: issueTitleSchema.optional(),
    description: issueDescriptionSchema.nullable().optional(),
    type: issueTypeKeySchema.optional(),
    statusId: idSchema.optional(),
    priority: issuePrioritySchema.optional(),
    assigneeId: personIdSchema.nullable().optional(),
    parentId: idSchema.nullable().optional(),
    labelIds: z.array(idSchema).max(20).optional(),
    startDate: calendarDateSchema.nullable().optional(),
    dueDate: calendarDateSchema.nullable().optional(),
    estimate: estimateSchema.nullable().optional(),
    sprintId: idSchema.nullable().optional(),
    releaseId: idSchema.nullable().optional(),
  })
  .strict();

/**
 * A move on a board or in a list (BUILD_SPEC §12.2): optionally to another status, and to a place in the project's
 * order — after the issue above it and before the one below it, either of which may be absent.
 */
export const moveIssueSchema = z
  .object({
    revision: z.number().int().positive().optional(),
    statusId: idSchema.optional(),
    after: idSchema.nullable().optional(),
    before: idSchema.nullable().optional(),
  })
  .strict();

/** What a clone copies besides the issue's own fields (BUILD_SPEC §12.1); comments never. */
export const cloneIssueSchema = z
  .object({ subtasks: z.boolean(), links: z.boolean(), attachments: z.boolean(), labels: z.boolean() })
  .strict();

/** An issue moved to another project (BUILD_SPEC §10.3): the status it goes to there, and a type where it needs one. */
export const projectMoveSchema = z
  .object({ projectId: idSchema, statusId: idSchema, type: issueTypeKeySchema.optional() })
  .strict();
export const projectMoveQuerySchema = z.object({ projectId: idSchema });

/**
 * An issue's customer (owner decision 14): a company and a contact of the organisation's directory at
 * Link, each by Link's id and the version of the record the person chose, or null for none.
 */
const directoryRecordRef = (id: RegExp) =>
  z.object({ id: z.string().regex(id), version: z.number().int().positive() }).strict();
export const setIssueCustomerSchema = z
  .object({
    company: directoryRecordRef(/^cmp_[0-9a-f]{32}$/).nullable(),
    contact: directoryRecordRef(/^ctc_[0-9a-f]{32}$/).nullable(),
  })
  .strict();
export type SetIssueCustomerInput = z.infer<typeof setIssueCustomerSchema>;

/** A search of the organisation's directory, as Link takes one (its docs/PRODUCT_API.md, "Search"). */
export const directorySearchSchema = z.object({ q: z.string().trim().min(1).max(200) }).strict();

/** What a list sorts by (BUILD_SPEC §12.4): any of an issue's own fields, each with the direction it starts in. */
export const ISSUE_SORTS = [
  "rank",
  "key",
  "title",
  "status",
  "priority",
  "type",
  "assignee",
  "estimate",
  "start",
  "due",
  "created",
  "updated",
  "resolved",
  /** Priority, then due date: what needs doing first (My work, BUILD_SPEC §12.7). */
  "urgency",
] as const;
export type IssueSort = (typeof ISSUE_SORTS)[number];

/** What a list groups by (BUILD_SPEC §12.4), each group with its total; an issue with several labels is in each. */
export const ISSUE_GROUPS = [
  "status",
  "category",
  "project",
  "assignee",
  "priority",
  "type",
  "epic",
  "sprint",
  "label",
] as const;
export type IssueGroup = (typeof ISSUE_GROUPS)[number];

/** What narrows a project's timeline (BUILD_SPEC §12.5): a query, or a saved filter, in the person's time. */
export const timelineQuerySchema = z.object({
  jql: z.string().max(2_000).optional(),
  filter: idSchema.optional(),
  tz: z.string().max(64).optional(),
});

/** At most this many days on one calendar: a month's weeks, whichever day they start on, with room to spare. */
export const CALENDAR_DAYS = 62;

/** The days a project's calendar shows (BUILD_SPEC §12.6), both included, and what narrows it, in the person's time. */
export const calendarQuerySchema = z
  .object({
    from: calendarDateSchema,
    to: calendarDateSchema,
    jql: z.string().max(2_000).optional(),
    filter: idSchema.optional(),
    tz: z.string().max(64).optional(),
  })
  .refine((query) => query.from <= query.to && daysBetween(query.from, query.to) < CALENDAR_DAYS, {
    message: `A calendar shows from a day to one on or after it, at most ${CALENDAR_DAYS} days`,
    path: ["to"],
  });

export const listIssuesQuerySchema = z.object({
  /** A status's id. */
  status: idSchema.optional(),
  category: statusCategorySchema.optional(),
  /** `true`: only issues not yet resolved. */
  open: z.enum(["true", "false"]).optional(),
  /** A person's id, `me`, or `unassigned`. */
  assignee: z.string().min(1).max(64).optional(),
  type: issueTypeKeySchema.optional(),
  priority: issuePrioritySchema.optional(),
  label: idSchema.optional(),
  parent: idSchema.optional(),
  q: z.string().trim().min(1).max(200).optional(),
  /** `true`: only issues an unresolved issue blocks. */
  blocked: z.literal("true").optional(),
  /** `true`: only issues changed in the last day. */
  recent: z.literal("true").optional(),
  /** A person's id, or `me`: the issues they reported. */
  reporter: z.string().min(1).max(64).optional(),
  /** `me`: the issues the caller watches. */
  watching: z.literal("me").optional(),
  /** Unresolved issues due on or before this date, those overdue too. */
  dueBefore: calendarDateSchema.optional(),
  /** A SteerQL query (BUILD_SPEC §13.1), read and bounded by its own parser. */
  jql: z.string().max(10_000).optional(),
  /** A saved filter's id (§12.9), run from its tree. */
  filter: idSchema.optional(),
  /** The person's timezone, for what a query's dates mean: "Europe/London". */
  tz: z.string().max(64).optional(),
  sort: z.enum(ISSUE_SORTS).default("rank"),
  /** Against the sort's own direction: newest first for dates, A to Z for words. */
  dir: z.enum(["asc", "desc"]).optional(),
  group: z.enum(ISSUE_GROUPS).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  /** Where the last page ended, as the previous page's `nextCursor` gave it. */
  cursor: z
    .string()
    .regex(/^[A-Za-z0-9_-]{1,400}$/)
    .optional(),
});

/** Full-text search (BUILD_SPEC §13.3): words, the last as it is typed, across what the person may read. */
export const searchQuerySchema = z
  .object({
    q: z.string().trim().min(1).max(200),
    limit: z.coerce.number().int().min(1).max(50).default(20),
    project: idSchema.optional(),
  })
  .strict();

/** A field's values to suggest as they are typed, in SteerQL's builder (BUILD_SPEC §13.1). */
export const queryValuesSchema = z
  .object({
    field: z.string().trim().min(1).max(40),
    q: z.string().max(100).default(""),
    project: idSchema.optional(),
  })
  .strict();

/** A SteerQL query to check as it is typed, for a list of one project or of every one. */
export const queryCheckSchema = z
  .object({
    jql: z.string().max(10_000),
    project: idSchema.optional(),
    tz: z.string().max(64).optional(),
  })
  .strict();

export type CreateIssueInput = z.infer<typeof createIssueSchema>;
export type UpdateIssueInput = z.infer<typeof updateIssueSchema>;
export type MoveIssueInput = z.infer<typeof moveIssueSchema>;
export type ListIssuesQuery = z.infer<typeof listIssuesQuerySchema>;
